import {
  AppStatusIncident,
  AppStatusIncidentInput,
} from '@src/main/modules/app_status/domain/appStatusIncident.model';
import {
  AppStatusIncidentRepository,
  AppStatusIncidentSearch,
} from '@src/main/modules/app_status/domain/appStatusIncident.repository';
import {
  toAppStatusIncident,
  toAppStatusIncidentFormData,
} from '@src/main/modules/app_status/infrastructure/appStatusIncident.mapper';
import { toAppStatusIncidentQueryParams } from '@src/main/modules/app_status/infrastructure/appStatusIncidentCriteria.mapper';
import { AppStatusIncidentResponseDto } from '@src/main/modules/app_status/infrastructure/dto/appStatusIncident.dto';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';

const path = '/app_status';

export const createAppStatusIncidentHttpRepository = (
  tokenProvider: TokenProvider,
): AppStatusIncidentRepository => new AppStatusIncidentHttpRepository(tokenProvider);

class AppStatusIncidentHttpRepository implements AppStatusIncidentRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findAll(criteria: AppStatusIncidentSearch): Promise<PageResponse<AppStatusIncident>> {
    const token = await this.getAuthToken();
    const response = await api.get<PageResponse<AppStatusIncidentResponseDto>>(
      `${path}/all`,
      token,
      toAppStatusIncidentQueryParams(criteria),
    );

    return { ...response, items: response.items.map(toAppStatusIncident) };
  }

  async create(input: AppStatusIncidentInput): Promise<AppStatusIncident> {
    const token = await this.getAuthToken();
    const response = await api.post<AppStatusIncidentResponseDto>(
      path,
      token,
      toAppStatusIncidentFormData(input),
      true,
    );
    return toAppStatusIncident(response);
  }

  async update(id: string, input: AppStatusIncidentInput): Promise<AppStatusIncident> {
    const token = await this.getAuthToken();
    const response = await api.put<AppStatusIncidentResponseDto>(
      `${path}/${id}`,
      token,
      toAppStatusIncidentFormData(input),
      true,
    );
    return toAppStatusIncident(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${path}/${id}`, token);
  }
}
